import React, { type FC, type JSX, type Ref, useEffect, useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { createResultPageSortResultPayload } from '@/app/common/starTrack/events/resultPageSortResult.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';

import { useSearchCommonPageData } from '@/app/components/search/searchCommonPageProvider.tsx';
import type { SortValues } from '@/app/common/memsearch/types.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import Form, { type ControlProps, type UpdateOnChangeProps } from '../../../../Form';
import {
  applyResultPageFilterEvent,
  removeAllFilters,
  removeEmptyParams,
  removeSearchParams,
  removeSingleFilter,
} from '../../../utils';
import verticalFiltersUpdateOnChange from '../../../verticalFilters/verticalFiltersUpdateOnChange';
import { RouteType } from '../../../../../Curiosity';
import verticalFilters from '../../../verticalFilters';
import getSearchParams from '../../../../../common/getSearchParams';
import { getMaxPrice } from '../../../verticalFilters/Fields/getPriceField/priceUtils';
import { Apply, Clear, CtaContainer, PillOverlayContainer } from './styled';
import { addAirportsToLocalStorage } from '../utils';
import type { DynamicFiltersResponse } from '../../../../../utils/api/dynamicFiltersResponseTypes';
import { sumClasses } from '../../../../../utils/misc';
import { updateFilters } from '../../../updateFilters';
import {
  disableControls,
  FILTERS_THAT_CAN_FILTER_ITSELF,
  getAppliedFilters,
  getCurrentFilterControl,
  HEIGHT_OF_DESKTOP_HEADER,
  updateFiltersWithCurrentOrder,
} from './utils';

const getHasScroll = (
  overlayRef: React.MutableRefObject<HTMLDivElement | undefined>,
  verticalControls: ControlProps[]
) => {
  if (canUseDOM) {
    const windowHeight = window.innerHeight;
    const availableWindowHeight = windowHeight - HEIGHT_OF_DESKTOP_HEADER;

    if (availableWindowHeight && overlayRef?.current?.offsetHeight) {
      const isTallerThanWindow = overlayRef?.current?.offsetHeight >= availableWindowHeight;
      return (verticalControls && verticalControls.length > 1) || isTallerThanWindow;
    }
  }
  return false;
};

type PillOverlayProps = {
  overlayRef: React.MutableRefObject<HTMLDivElement | undefined>;
  routeType: RouteType;
  isWaiting: boolean;
  control: string | undefined;
  filterName: string;
  dynamicFiltersResponse?: DynamicFiltersResponse;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hasCta?: boolean;
};
const PillOverlay: FC<PillOverlayProps> = ({
  overlayRef,
  routeType,
  isWaiting,
  control,
  filterName,
  hasCta,
  dynamicFiltersResponse: dynamicFiltersResponseDefault,
  setOpen,
}): JSX.Element => {
  const {
    searchInfoData,
    searchInfoDetailData,
    localization,
    curiosityParams,
    commonData,
    localization: { brand },
  } = usePageWrapperData();
  const { productsData } = useSearchPageData();
  const { starTrack } = useCommonClient();
  const { t } = useTranslation();
  const { isMobile, isTablet, isDesktop, externalHeaderEnabled } = useCommonClient();
  const { handleSortChanged, handleFiltersChanged } = useSearchCommonPageData();
  const { hasPreselectedFlight } = useSearchDealsPageData();
  const { searchMode } = searchInfoData;
  const { lang, currency } = localization;
  const formRef = useRef<Form>(undefined);
  const [searchParamsState, setSearchParamsState] = useState(getSearchParams());
  let appliedFilters = removeSearchParams(searchParamsState);
  const [oldFilters] = useState(JSON.parse(JSON.stringify(appliedFilters)));
  const [className, setClassName] = useState<string>('');
  const [dynamicFiltersResponse, setDynamicFiltersResponse] = useState<DynamicFiltersResponse | undefined>(
    dynamicFiltersResponseDefault
  );

  useEffect(() => {
    if (overlayRef?.current) {
      overlayRef?.current?.focus();
    }
  }, [overlayRef]);

  const verticalControlsProps = {
    searchMode,
    searchParams: searchParamsState,
    isMap: false,
    isBeachHoliday: searchParamsState.destination === 'L1',
    isHorizontal: true,
    commonData,
    localization,
    currency,
    curiosityParams,
    productsData,
    dynamicFiltersResponse,
    isWaiting,
  };

  const verticalControls = getCurrentFilterControl(
    verticalFilters({
      ...verticalControlsProps,
      searchParams: searchParamsState,
      searchInfoData,
      searchInfoDetailData,
      dynamicFiltersResponse,
      isMobile,
      hasPreselectedFlight,
      t,
    }),
    control
  );

  const applyFilters = () => {
    const applied = removeEmptyParams(appliedFilters);
    const searchParamsFresh = getSearchParams();
    handleFiltersChanged({ searchParams: searchParamsFresh, appliedFilters: applied });
    setOpen(false);
  };

  const updateOnChange = async ({ e, updatedControls }: UpdateOnChangeProps) => {
    const maxPriceSlider = getMaxPrice(lang, currency);
    const appliedFiltersUpdated = verticalFiltersUpdateOnChange(
      e,
      appliedFilters,
      maxPriceSlider,
      commonData,
      localization,
      curiosityParams,
      routeType,
      searchInfoDetailData,
      t,
      dynamicFiltersResponse
    );

    if (e.target.name === 'sort') {
      const newSortValue: SortValues = e.target.value as SortValues;
      searchParamsState[URLParameters.SORT] = newSortValue;
      const searchParamsUpdated = { ...removeAllFilters(searchParamsState), ...appliedFiltersUpdated };
      starTrack.resultPageSortResult(
        createResultPageSortResultPayload({ searchParams: searchParamsUpdated, localization })
      );
      handleSortChanged({ newSortValue, searchParamsUpdated });
      setOpen(false);
    }

    const newQuery = { ...removeAllFilters(searchParamsState), ...appliedFiltersUpdated };
    setSearchParamsState(newQuery);

    if (FILTERS_THAT_CAN_FILTER_ITSELF.includes(e.target.name as URLParameters) && dynamicFiltersResponse) {
      const getControls = (dynamicFiltersResponseFromApi: DynamicFiltersResponse): ControlProps[] =>
        updateFiltersWithCurrentOrder(
          updatedControls,
          getCurrentFilterControl(
            verticalFilters({
              ...verticalControlsProps,
              searchParams: newQuery,
              searchInfoData,
              searchInfoDetailData,
              dynamicFiltersResponse: dynamicFiltersResponseFromApi,
              isMobile,
              hasPreselectedFlight,
              t,
            }),
            control
          )
        );

      updateFilters({
        searchParams: newQuery,
        searchInfoData,
        getControls,
        setDynamicFiltersResponse,
        formRef,
        commonData,
        localization,
        curiosityParams,
      });

      return disableControls(updatedControls);
    }

    addAirportsToLocalStorage({ e, productsData });
    return updatedControls;
  };

  const hasScroll = getHasScroll(overlayRef, verticalControls);

  const handleApplyClicked = () => {
    applyResultPageFilterEvent({
      oldFilters,
      appliedFilters,
      searchParams: searchParamsState,
      starTrack,
      localization,
    });

    applyFilters();
  };

  const handleClearClicked = () => {
    appliedFilters = removeSingleFilter(appliedFilters, filterName);
    formRef?.current?.resetForm();
  };

  useEffect(() => {
    if (isDesktop || (isTablet && routeType === RouteType.SEARCHRESULTS)) {
      setTimeout(() => {
        const bounding = overlayRef?.current?.getBoundingClientRect();
        const rightSideIsOut = (bounding?.right || 0) > (window.innerWidth || document.documentElement.clientWidth);
        if (rightSideIsOut) {
          setClassName('pill-overlay-container');
        }
      }, 1);
    }
  }, [overlayRef.current]);

  useOnclickOutside(
    () => {
      setOpen(false);
    },
    {
      // @ts-ignore
      refs: [overlayRef],
      ignoreClass: [`Pill-${control}`],
      eventTypes: ['mousedown', 'touchstart', 'keydown'], // Add keydown for keyboard navigation
    }
  );

  const getColumns = () => {
    if (verticalControls && verticalControls?.[0]?.options && verticalControls?.[0].options?.length > 6) return 2;
    return 1;
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <PillOverlayContainer
      ref={overlayRef}
      className={sumClasses([control || '', className])}
      onKeyDown={handleOnKeyDown}
      localization={localization}
      tabIndex={0}
      brand={brand}
      columns={getColumns()}
      hasScroll={hasScroll}
      externalHeaderEnabled={externalHeaderEnabled}
    >
      <Form
        ref={formRef as Ref<Form>}
        controls={verticalControls}
        resetKeyOnUpdate={false}
        updateOnChange={updateOnChange}
        formClassName={sumClasses([
          'openx-ui-filters openx-ui-filters-overlay',
          hasScroll ? 'openx-ui-filters-with-scroll' : '',
        ])}
      />
      {hasCta && (
        <CtaContainer fixedBottom={hasScroll}>
          <Clear onClick={handleClearClicked} disabled={!getAppliedFilters(appliedFilters, filterName)}>
            {t('vacation.oss.filters.reset')}
          </Clear>
          <Apply onClick={handleApplyClicked} disabled={false}>
            {t('vacation.oss.filters.apply')}
          </Apply>
        </CtaContainer>
      )}
    </PillOverlayContainer>
  );
};

PillOverlay.displayName = 'PillOverlay';

export default PillOverlay;
